import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { useArticleTypes } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useArticleTypes';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { useTrigger } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useTrigger';
import { CartFormActions } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/CartFormActions';
import { MinimalFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/MinimalFormItem';
import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { useGetApplicationSteps } from '@/components/modules/Application/hooks/useGetApplicationSteps';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export const useCartForm = () => {
  const {
    actions: { updateApplicationContext },
  } = useApplicationContext();
  const { updateStepsAndGoNext } = useApplicationNavigation();
  const { getSteps } = useGetApplicationSteps();
  const { cart, setCart, removeCartItem, doSubmitCart, clearSubmitCart } = useCart();
  const { defaultFormItem } = useArticleTypes();

  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { isValid: isFormValid },
  } = useFormContext();

  const {
    fields: itemFields,
    update: itemFieldsUpdate,
    append: itemFieldsAppend,
    remove: itemFieldsRemove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const items = watch('items');

  const updateCart = () => {
    setCart(items);
  };
  const { trigger: doUpdateCart } = useTrigger({ callback: updateCart });

  useEffect(() => {
    if (!cart) updateCart();
  }, []);

  useEffect(() => {
    if (doSubmitCart) {
      handleSubmit(onSubmit)();
      clearSubmitCart();
    }
  }, [doSubmitCart]);

  useEffect(() => {
    const subscription = watch((_, { type }) => {
      if (type == 'change') doUpdateCart();
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  //Update in context form valid state
  useEffect(() => {
    updateApplicationContext({ isCartFormValid: isFormValid });
  }, [isFormValid]);

  const cartFormActions = {
    removeItem: (index) => {
      removeCartItem(index);
      itemFieldsRemove(index);
    },
    appendItem: () => {
      itemFieldsAppend(defaultFormItem as MinimalFormItem);
      doUpdateCart();
    },
    updateItem: (index, data) => {
      itemFieldsUpdate(index, data);
      clearErrors(`items.${index}`);
    },
    saveInCart: () => {
      updateCart();
    },
  } as CartFormActions;

  const onSubmit = (data) => {
    if (!cart) return;

    const newCart = CartEntity.updateCartItemsFromMinimalForm(data.items, cart);
    setCart(data.items);
    updateStepsAndGoNext(getSteps(newCart));
  };

  return { cart, items, isFormValid, itemFields, cartFormActions, updateApplicationContext };
};
